import { useCallback, useEffect, useState } from 'react'
import {
  fetchEscalations, formatCount, formatMinutes, formatQuantity, formatTime, nameIn,
  pluralise, translateError,
  type Escalation,
} from '@hk/core'
import {
  Card, CardHeader, ErrorState, LoadingState, useLocale,
} from '@hk/ui'
import { STRINGS } from '../i18n/strings'

/** The web's own placeholder filler — the same `.replace` every other screen here uses,
 *  named once because this file does it five times. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-61 / M-15 — NEEDS YOU.

   Pack 6 puts this on the handset and says, underneath it: *"These are the same four cards
   a supervisor sees on the web. Nothing is supervisor-only on one platform and missing
   from the other — a supervisor who has to go and find a computer to act on something they
   were just alerted to will stop trusting the alert."*

   **THAT SENTENCE IS THE REASON THIS FILE EXISTS**, and the reason it was written in the
   same sprint as the handset's. Sprint 45 shipped one of the four cards on M-12 alone and
   said in writing that the rest would wait until both platforms could have them.

   **IT SORTS NOTHING.** The order comes from `IEscalationQuery` and so does the handset's.
   Two screens each deciding what matters most this morning is precisely the disagreement
   the pack is warning about, and it would show up as a supervisor being told different
   things by the phone in his hand and the screen on his desk.
   ========================================================================= */

export function Escalations() {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [cards, setCards] = useState<Escalation[]>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setCards(await fetchEscalations())
      setError(undefined)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (loading) return <LoadingState />

  if (!cards) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => void load()}
      />
    )
  }

  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold">{t.needsYou}</h1>
        <p className="text-sm text-muted-foreground">
          {cards.length === 0
            ? t.nothingNeedsYou
            : pluralise(t.thingsNeedYou, cards.length, locale,
                formatCount(cards.length, locale))}
        </p>
      </header>

      {/* **AN EMPTY SCREEN IS A GOOD MORNING, AND SAYS SO.** A blank panel reads as one
          that failed to load, and somebody who thinks the alerts are broken stops opening
          them — the same outcome as having no alerts at all. */}
      {cards.length === 0 ? (
        <Card>
          <div className="space-y-1 p-6">
            <p className="text-sm font-medium">{t.nothingNeedsYou}</p>
            <p className="text-sm text-muted-foreground">{t.nothingNeedsYouBody}</p>
          </div>
        </Card>
      ) : (
        cards.map((card, i) => (
          <EscalationCard
            key={`${card.code}:${card.roomNumber ?? ''}:${card.subjectName ?? ''}:${i}`}
            card={card}
          />
        ))
      )}
    </div>
  )
}

/**
 * **THE SAME FOUR SHAPES THE HANDSET DRAWS, IN THE WEB'S OWN WORDS.**
 *
 * **EXPORTED SINCE SPRINT 61**, because W-10 draws the same four cards under *"Needs a
 * decision"*. A second copy would have been the #145 mistake at the level of a component:
 * the day one card grows a line, the dashboard is the screen that keeps the old one and
 * nothing fails.
 *
 * The strings are the web's, not shared with the handset's — the project's standing rule,
 * because the moment one of the two needs to change, a shared string forces a decision
 * nobody wants to take under pressure. What IS shared is the data, the order and the rule
 * about which fields make which card, and those are the parts that could disagree.
 */
export function EscalationCard({ card }: { card: Escalation }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const title =
    card.code === 'alert.soldRoomOutOfOrder' ? t.alert.soldRoomOutOfOrder
    : card.code === 'alert.requestsLate'
      ? pluralise(t.alert.requestsLate, card.count, locale, formatCount(card.count, locale))
    : card.code === 'alert.repeatFailure' ? t.alert.repeatFailure
    : fill(t.alert.stockBelowPar, {
        item: nameIn(locale, card.subjectName ?? '', card.subjectNameAr),
      })

  /* A card with no clock is not "0m": a guest at the door is "now" and a shelf is
     "today". **And a repeat failure is "ago", not "over"** — its clock is how long since
     the last one, which is not lateness. Nobody is late; a man has done the same thing
     wrong twice. */
  const when =
    card.minutesOver === null
      ? card.code === 'alert.soldRoomOutOfOrder' ? t.now : t.today
      : card.code === 'alert.repeatFailure'
        ? fill(t.minutesAgo, { minutes: formatMinutes(card.minutesOver, locale) })
        : fill(t.minutesOver, { minutes: formatMinutes(card.minutesOver, locale) })

  const body =
    card.code === 'alert.soldRoomOutOfOrder'
      ? fill(t.roomAndArrival, {
          room: card.roomNumber ?? '',
          time: card.arrivalUtc ? formatTime(new Date(card.arrivalUtc), locale) : '—',
        })
      : card.code === 'alert.requestsLate'
        /* WHAT THEY WANTED, THEN WHICH DOORS. The rooms are what he acts on; the category
           is what he is carrying, and it is translated like every other name here. */
        ? [nameIn(locale, card.subjectName ?? '', card.subjectNameAr),
           ...card.references].filter(Boolean).join(' · ')
        : card.code === 'alert.repeatFailure'
          ? fill(t.roomItemAndPerson, {
              room: card.roomNumber ?? '',
              item: nameIn(locale, card.subjectName ?? '', card.subjectNameAr),
              name: nameIn(locale, card.personName ?? '', card.personNameAr),
            })
          : fill(t.ofPar, {
              quantity: formatQuantity(card.quantity ?? 0, locale),
              par: formatQuantity(card.parLevel ?? 0, locale),
            })

  const footnote =
    card.code === 'alert.requestsLate' && (card.neverAccepted ?? 0) > 0
      ? pluralise(t.neverAccepted, card.neverAccepted!, locale,
          formatCount(card.neverAccepted!, locale))
      : card.code === 'alert.repeatFailure'
        ? pluralise(t.failuresThisWeek, card.count, locale, formatCount(card.count, locale))
        : card.code === 'alert.stockBelowPar' && card.daysOfCover !== null
          ? pluralise(t.daysOfCover, card.daysOfCover, locale,
              formatCount(card.daysOfCover, locale))
          : null

  return (
    <Card className="border-[var(--hk-sv-ooo-border)]">
      <CardHeader
        title={title}
        actions={
          <span
            className="shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-bold"
            style={{
              color: 'var(--hk-sv-ooo-text)',
              borderColor: 'var(--hk-sv-ooo-border)',
            }}
          >
            {when}
          </span>
        }
      />
      <div className="space-y-1 px-6 pb-6">
        <p className="text-sm text-muted-foreground">{body}</p>
        {footnote ? <p className="text-xs text-muted-foreground">{footnote}</p> : null}

        {/* **CQ-142 — AND THE LAUNDRY CANNOT SAVE IT.** Under the days-of-cover line and
            not instead of it: the two sentences are a fact and its consequence, and a
            supervisor who is being asked to act this morning should see what the product
            is reasoning from. Drawn in the alert's own colour rather than the muted grey
            of the line above, because it is the only line on this card that asks for
            something. */}
        {card.laundryCannotReplace ? (
          <p
            className="text-xs font-medium"
            style={{ color: 'var(--hk-sv-ooo-text)' }}
          >
            {t.laundryTooSlow}
          </p>
        ) : null}

        {/* **HIS OWN WORDS, AND THEIR OWN DIRECTION.** Never translated, and isolated so
            an Arabic paragraph does not throw the sentence's full stop to the far end of
            the line from the word it belongs to. */}
        {card.detail ? (
          <p
            dir="auto"
            className="text-xs text-muted-foreground"
            style={{ unicodeBidi: 'isolate' }}
          >
            {card.detail}
          </p>
        ) : null}
      </div>
    </Card>
  )
}
